import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PUBLIC_MEDIA } from "../../lib/constants";

/**
 * Showcase — 6 full-bleed split-screen rows. Each row spans 100vw with no horizontal
 * padding; the left/right halves alternate between image and copy so the page feels
 * cinematic and editorial. On mobile, each row stacks (image first, copy below).
 */

const ROWS = [
  {
    eyebrow: "Collection · 01",
    title: "Luxury Kitchens",
    italic: "Heart of the home.",
    body:
      "Handleless lacquer fronts, Blum soft-close hardware, Italian-designed islands engineered for Canadian climates. Every drawer is built to outlast the trends.",
    img: PUBLIC_MEDIA("theo-oppolia/public/catalog/theo_oppolia_whole_house/page_073.webp"),
    href: "/products?cat=Kitchen%20Cabinets",
    cta: "Explore Kitchens",
    side: "left",
    bg: "bg-[#0A0A0A]",
    fg: "text-[#F5F2EB]",
    sub: "text-[#F5F2EB]/70",
  },
  {
    eyebrow: "Collection · 02",
    title: "Wardrobes & Closets",
    italic: "Architecture for getting dressed.",
    body:
      "Backlit aluminum profiles, leather drawer pulls, velvet jewellery trays. Walk-in or wall-mounted — configured to your exact dimensions.",
    img: PUBLIC_MEDIA("theo-oppolia/public/catalog/theo_oppolia_whole_house/page_025.webp"),
    href: "/products?cat=Bedroom%20Wardrobes",
    cta: "Explore Wardrobes",
    side: "right",
    bg: "bg-[#FDFBF7]",
    fg: "text-[#0A0A0A]",
    sub: "text-[#0A0A0A]/70",
  },
  {
    eyebrow: "Collection · 03",
    title: "Living Room Panels",
    italic: "Walls that tell a story.",
    body:
      "Floor-to-ceiling fluted oak media walls with hidden cable management, push-to-open doors, and integrated mood lighting.",
    img: PUBLIC_MEDIA("theo-oppolia/public/catalog/theo_oppolia_whole_house/page_049.webp"),
    href: "/products?cat=TV%20Panels",
    cta: "Explore Panels",
    side: "left",
    bg: "bg-[#0A0A0A]",
    fg: "text-[#F5F2EB]",
    sub: "text-[#F5F2EB]/70",
  },
  {
    eyebrow: "Collection · 04",
    title: "Doors & Windows",
    italic: "Threshold-grade engineering.",
    body:
      "Aluminum-clad pivot doors, slim-frame double-glazed windows, concealed hinges. Tested to Canadian climate standards.",
    img: PUBLIC_MEDIA("theo-oppolia/public/catalog/theo_oppolia_whole_house/page_117.webp"),
    href: "/products?cat=Doors%20%26%20Windows",
    cta: "Explore Doors & Windows",
    side: "right",
    bg: "bg-[#F5F2EB]",
    fg: "text-[#0A0A0A]",
    sub: "text-[#0A0A0A]/70",
  },
  {
    eyebrow: "Collection · 05",
    title: "Custom Furniture",
    italic: "Office, living, dining — bespoke.",
    body:
      "Smoked oak veneer, full-leather drawer inserts, integrated cable management. Every piece drawn for your room.",
    img: PUBLIC_MEDIA("theo-oppolia/public/photos/opp_2.webp"),
    href: "/products?cat=Office%20Furniture",
    cta: "Explore Furniture",
    side: "left",
    bg: "bg-[#0A0A0A]",
    fg: "text-[#F5F2EB]",
    sub: "text-[#F5F2EB]/70",
  },
  {
    eyebrow: "Trade · Builder · Developer",
    title: "Builder & Developer Solutions",
    italic: "Volume terms. Single accountable supplier.",
    body:
      "Project pricing, dedicated account management, signed-off shop drawings, and on-site installation across Canada. From a single luxury home to a 200-unit tower.",
    img: PUBLIC_MEDIA("theo-oppolia/public/photos/opp_5.webp"),
    href: "/contact",
    cta: "Talk to our Trade Desk",
    side: "right",
    bg: "bg-[#FDFBF7]",
    fg: "text-[#0A0A0A]",
    sub: "text-[#0A0A0A]/70",
    accent: true,
  },
];

function Row({ row, index }) {
  const imgFirst = row.side === "left";
  return (
    <section
      className={`${row.bg} ${row.fg} w-screen relative overflow-hidden`}
      data-testid={`showcase-row-${index}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 min-h-[68vh] md:min-h-[80vh]">
        <motion.div
          initial={{ opacity: 0, scale: 1.04 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className={`relative overflow-hidden ${imgFirst ? "md:order-1" : "md:order-2"} aspect-[4/3] md:aspect-auto`}
        >
          <img
            src={`${row.img}?w=1200`}
            srcSet={`${row.img}?w=480 480w, ${row.img}?w=800 800w, ${row.img}?w=1200 1200w, ${row.img}?w=1600 1600w`}
            sizes="(max-width: 767px) 100vw, 50vw"
            alt={row.title}
            width="1600"
            height="1200"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent md:bg-none" />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className={`${imgFirst ? "md:order-2" : "md:order-1"} flex flex-col justify-center px-6 sm:px-10 md:px-16 lg:px-24 xl:px-32 py-14 md:py-0`}
        >
          <p className="overline text-[#B08858] mb-5 tracking-[0.22em]">{row.eyebrow}</p>
          <h2 className="font-display leading-[1.02] tracking-tight text-5xl md:text-6xl lg:text-7xl xl:text-[80px]">
            {row.title}
            <br />
            <em className="not-italic font-light text-[#B08858]">{row.italic}</em>
          </h2>
          <p className={`mt-7 text-base md:text-lg leading-relaxed max-w-[560px] ${row.sub}`}>{row.body}</p>
          <div className="mt-10">
            <Link
              to={row.href}
              data-testid={`showcase-cta-${index}`}
              className={`inline-flex items-center gap-3 group text-sm tracking-[0.22em] uppercase font-medium ${row.accent ? "text-[#B08858]" : ""}`}
            >
              <span>{row.cta}</span>
              <span className={`w-12 h-12 border ${row.fg === "text-[#F5F2EB]" ? "border-white/40" : "border-[#0A0A0A]/30"} group-hover:bg-[#B08858] group-hover:border-[#B08858] group-hover:text-white transition-all duration-500 flex items-center justify-center`}>
                <ArrowUpRight size={16} />
              </span>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default function Showcase() {
  return (
    <div data-testid="showcase-sections" className="w-screen">
      {ROWS.map((row, i) => (
        <Row key={row.title} row={row} index={i} />
      ))}
    </div>
  );
}
